import React, { useState, useEffect } from "react";
import Modal from "../modal/modal";
import Dropdown from "../modal/dropdown";
import DocumentPreviewModal from "../modal/DocumentPreviewModal";
import ConfirmModal from "../employee/delete_modal";
import { getTaskAttachments, deleteTaskAttachment } from "../services/taskservice";
import { toast } from "react-toastify";
import api from "../services/api";
import "./task.css";

import { TaskStatus, TaskPriority } from "../../constants/constants";

const PRIORITIES = [
  { label: "Low", value: TaskPriority.LOW },
  { label: "Medium", value: TaskPriority.MEDIUM },
  { label: "High", value: TaskPriority.HIGH },
];
const STATUSES = [
  { label: "To-Do", value: TaskStatus.TODO },
  { label: "In Progress", value: TaskStatus.IN_PROGRESS },
  { label: "Done", value: TaskStatus.DONE },
  { label: "On Hold", value: TaskStatus.ON_HOLD },
  { label: "Cancelled", value: TaskStatus.CANCELLED },
];

export default function TaskModal({ isEdit = false, existing = null, onSave, onClose }) {
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams]         = useState([]);
  const [projects, setProjects]   = useState([]);
  const [assignType, setAssignType] = useState("employee");

  const [form, setForm] = useState({
    title: "", description: "", priority: TaskPriority.MEDIUM, status: TaskStatus.TODO,
    start_date: "", due_date: "", assigned_to: "", team: "",
    project: "", tags: "", attachments: [],
  });
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [deleteAttachmentId, setDeleteAttachmentId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("dropdowns/employees/"),
      api.get("dropdowns/teams/"),
      api.get("dropdowns/projects/"),
    ]).then(([e, t, p]) => {
      setEmployees(e.data.data || e.data || []);
      setTeams(t.data.data || t.data || []);
      setProjects(p.data.data || p.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit && existing) {
      setForm({
        title: existing.title || "",
        description: existing.description || "",
        priority: existing.priority || TaskPriority.MEDIUM,
        status: existing.status || TaskStatus.TODO,
        start_date: existing.start_date || "",
        due_date: existing.due_date || "",
        assigned_to: existing.assigned_to || "",
        team: existing.team || "",
        project: existing.project || "",
        tags: existing.tags || "",
        attachments: [],
      });
      if (existing.team) setAssignType("team");

      // Fetch existing attachments
      getTaskAttachments(existing.id)
        .then(res => {
          setExistingAttachments(res.data || res || []);
        })
        .catch(() => toast.error("Failed to load existing attachments"));
    }
  }, [isEdit, existing]);

  const handleDeleteAttachment = async () => {
    try {
      await deleteTaskAttachment(existing.id, deleteAttachmentId);
      toast.success("Attachment deleted");
      setExistingAttachments(prev => prev.filter(a => a.id !== deleteAttachmentId));
    } catch {
      toast.error("Failed to delete attachment");
    } finally {
      setDeleteAttachmentId(null);
    }
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.title) errs.title = "Task title is required";
    if (assignType === "employee" && !form.assigned_to) errs.assigned_to = "Please assign to an employee";
    if (assignType === "team" && !form.team) errs.team = "Please assign to a team";
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return setError("Please check the field errors below.");
    }
    setError("");
    setFieldErrors({});

    const payload = { ...form };
    if (assignType === "employee") {
      payload.team = null;
    } else {
      payload.assigned_to = null;
    }

    try {
      await onSave(payload);
    } catch (err) {
      const respData = err?.response?.data;
      const backendErrors = respData?.errors || respData;
      if (backendErrors && typeof backendErrors === "object" && !backendErrors.message) {
        const extracted = {};
        for (const [key, val] of Object.entries(backendErrors)) {
          extracted[key] = Array.isArray(val) ? val[0] : val;
        }
        setFieldErrors(extracted);
      } else {
        setError(respData?.message || "Failed to save task");
      }
    }
  };

  return (
    <Modal title={isEdit ? "Edit Task" : "Create Task"} onClose={onClose}>
      {error && <div className="form-error">{error}</div>}
      <div className="employee-form">
        <div className="employee-form-scroll">
          <h4 className="section-title">Task Details</h4>
          <div className="grid-1">
            <div>
              <input className={fieldErrors.title ? "input-error" : ""} placeholder="Task Title *" value={form.title} onChange={(e) => { setForm({ ...form, title: e.target.value }); if (fieldErrors.title) setFieldErrors({ ...fieldErrors, title: null }); }} />
              {fieldErrors.title && <div className="field-error">{fieldErrors.title}</div>}
            </div>
            <div>
              <textarea className={fieldErrors.description ? "input-error" : ""} placeholder="Description" value={form.description} onChange={(e) => { setForm({ ...form, description: e.target.value }); if (fieldErrors.description) setFieldErrors({ ...fieldErrors, description: null }); }} />
              {fieldErrors.description && <div className="field-error">{fieldErrors.description}</div>}
            </div>
          </div>

          <h4 className="section-title">Assignment</h4>
          <div className="grid-2">
            <Dropdown
              options={[{ label: "Assign to Employee", value: "employee" }, { label: "Assign to Team", value: "team" }]}
              value={assignType}
              onChange={setAssignType}
              placeholder="Assign Type"
            />
            {assignType === "employee" ? (
              <div>
                <Dropdown
                  options={employees.map((e) => ({ label: `${e.full_name || e.name} (${e.emp_id})`, value: e.id }))}
                  value={form.assigned_to}
                  onChange={(val) => { setForm({ ...form, assigned_to: val }); if (fieldErrors.assigned_to) setFieldErrors({ ...fieldErrors, assigned_to: null }); }}
                  placeholder="Select Employee"
                />
                {fieldErrors.assigned_to && <div className="field-error">{fieldErrors.assigned_to}</div>}
              </div>
            ) : (
              <div>
                <Dropdown
                  options={teams.map((t) => ({ label: t.name, value: t.id }))}
                  value={form.team}
                  onChange={(val) => { setForm({ ...form, team: val }); if (fieldErrors.team) setFieldErrors({ ...fieldErrors, team: null }); }}
                  placeholder="Select Team"
                />
                {fieldErrors.team && <div className="field-error">{fieldErrors.team}</div>}
              </div>
            )}
            <Dropdown
              options={projects.map((p) => ({ label: p.name, value: p.id }))}
              value={form.project}
              onChange={(val) => setForm({ ...form, project: val })}
              placeholder="Project (Optional)"
            />
          </div>

          <h4 className="section-title">Schedule & Priority</h4>
          <div className="grid-2">
            <div>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              {fieldErrors.start_date && <small className="field-error-text" style={{ color: "var(--danger)", display: "block", marginTop: "4px" }}>{fieldErrors.start_date}</small>}
            </div>
            <div>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              {fieldErrors.due_date && <small className="field-error-text" style={{ color: "var(--danger)", display: "block", marginTop: "4px" }}>{fieldErrors.due_date}</small>}
            </div>
            <Dropdown
              options={PRIORITIES}
              value={form.priority}
              onChange={(val) => setForm({ ...form, priority: val })}
              placeholder="Priority"
            />
            <Dropdown
              options={STATUSES}
              value={form.status}
              onChange={(val) => setForm({ ...form, status: val })}
              placeholder="Status"
            />
          </div>

          <h4 className="section-title">Additional</h4>
          <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />

          <h4 className="section-title" style={{ marginTop: 16 }}>Attachments</h4>
          {existingAttachments.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <strong style={{ fontSize: "0.85rem", color: "var(--text-main)", display: "block", marginBottom: "8px" }}>Uploaded Attachments:</strong>
              <ul style={{ fontSize: 13, listStyle: "none", padding: 0 }}>
                {existingAttachments.map(att => (
                  <li key={att.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, padding: "8px", background: "var(--bg-main)", borderRadius: "6px" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontWeight: "500", color: "var(--text-main)" }}>{att.original_filename}</span>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>{att.file_size_human} • Uploaded by {att.uploaded_by_name}</span>
                    </div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                      <button 
                        type="button" 
                        onClick={() => setPreviewDoc(att)} 
                        style={{ background: "transparent", border: "none", color: "var(--primary)", cursor: "pointer", fontSize: "0.85rem", fontWeight: "600" }}
                      >View</button>
                      <a href={att.download_url || att.file_url} download rel="noreferrer" style={{ color: "var(--primary)", textDecoration: "none", fontSize: "0.85rem", fontWeight: "600" }}>Download</a>
                      <button 
                        type="button" 
                        style={{ background: "transparent", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: "0.85rem", fontWeight: "600" }}
                        onClick={() => setDeleteAttachmentId(att.id)}
                      >Delete</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <input 
            type="file" 
            multiple 
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,text/plain"
            onChange={(e) => {
              const files = Array.from(e.target.files);
              setForm({ ...form, attachments: [...form.attachments, ...files] });
            }} 
            style={{ marginBottom: 8 }}
          />
          {form.attachments.length > 0 && (
            <ul style={{ fontSize: 12, color: "var(--text-secondary)", listStyle: "none", padding: 0 }}>
              {form.attachments.map((file, i) => (
                <li key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span>{file.name} (Pending Upload)</span>
                  <button 
                    type="button" 
                    style={{ background: "transparent", border: "none", color: "var(--danger)", cursor: "pointer" }}
                    onClick={() => setForm({ ...form, attachments: form.attachments.filter((_, idx) => idx !== i) })}
                  >✕</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="modal-footer">
        <div className="modal-actions">
          <button className="primary" onClick={handleSave}>Save</button>
          <button className="secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>

      {deleteAttachmentId && (
        <ConfirmModal
          title="Delete Attachment"
          message="Are you sure you want to delete this attachment? This action cannot be undone."
          onConfirm={handleDeleteAttachment}
          onCancel={() => setDeleteAttachmentId(null)}
        />
      )}

      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </Modal>
  );
}
